import Button from '../Button.jsx';
import {
  buildHeaderContentGridTemplateColumns,
  headerActionsClass,
  headerBackButtonClass,
  headerBackButtonIconSize,
  headerBackButtonSize,
  headerContentGridClass,
  headerContentGridCompressClass,
  headerDescriptionCellClass,
  headerDescriptionClass,
  headerCompressHeightClass,
  headerIconSlotClass,
  headerLeftClass,
  headerRootClass,
  headerTitleClass,
  headerTitleRowCellClass,
  normalizeHeaderSize,
  resolveHeaderContentColumnIndex,
  resolveHeaderIconColumnIndex,
  resolveHeaderVisibility,
} from './headerClasses.js';
import {
  renderHeaderCta,
  renderHeaderIcon,
  renderHeaderTag,
  resolveCtaSize,
} from './headerUtils.jsx';
import { ArrowLeft } from 'lucide-react';

/**
 * Page header aligned with Figma Design System — AIMS OS (node 7995:4267).
 *
 * Variants: `L`, `M`, `compress` (28px row — no description; title same as M, tag Badge small).
 * `PageHeader` remains available for legacy usage; migrate to `Header` incrementally.
 *
 * @example
 * <Header
 *   size="L"
 *   title="Workspaces"
 *   showDescription
 *   description="Manage your organization tenants"
 *   showTag
 *   tag={{ label: 'Active', variant: 'success' }}
 *   showPrimaryCta
 *   primaryCta={{ label: 'Create', onClick: () => {}, leftIcon: Plus }}
 *   showSecondaryCta
 *   secondaryCta={{ label: 'Import', variant: 'ghost', onClick: () => {} }}
 *   showBackButton={false}
 * />
 *
 * Tag and CTAs render {@link Badge} and {@link Button} from config objects.
 * Back button uses {@link Button} `variant="tertiary"` (icon-only).
 * `primaryCta` defaults to Button `variant="primary"`; `secondaryCta` to `variant="secondary"`.
 * Override with `variant` (and `size`) on each CTA config. Pass React nodes only for non-standard actions (e.g. `Dropdown`).
 */
const Header = ({
  size = 'L',
  title,
  showBackButton = true,
  onBack,
  backButtonAriaLabel = 'Go back',
  showDescription = true,
  description,
  showTag = true,
  tag,
  showPrimaryCta = true,
  primaryCta,
  showSecondaryCta = false,
  secondaryCta,
  showIcon = false,
  icon,
  className = '',
  titleClassName = '',
  descriptionClassName = '',
  ...props
}) => {
  const normalizedSize = normalizeHeaderSize(size);
  const visibility = resolveHeaderVisibility(normalizedSize, {
    showBackButton,
    showDescription,
    showTag,
    showIcon,
    showPrimaryCta,
    showSecondaryCta,
  });

  const ctaSize = resolveCtaSize(normalizedSize);
  const isCompress = normalizedSize === 'compress';

  const resolvedDescription =
    visibility.showDescription && description ? description : null;
  const resolvedTag =
    visibility.showTag && tag ? renderHeaderTag(tag, normalizedSize) : null;
  const resolvedIcon =
    visibility.showIcon && icon
      ? renderHeaderIcon(icon, isCompress ? 'h-4 w-4' : 'h-5 w-5')
      : null;

  const resolvedPrimaryCta =
    visibility.showPrimaryCta && primaryCta
      ? renderHeaderCta(primaryCta, { variant: 'primary', size: ctaSize })
      : null;

  const resolvedSecondaryCta =
    visibility.showSecondaryCta && secondaryCta
      ? renderHeaderCta(secondaryCta, { variant: 'secondary', size: ctaSize })
      : null;

  const hasActions = Boolean(resolvedPrimaryCta || resolvedSecondaryCta);
  const hasBack = Boolean(visibility.showBackButton && onBack);
  const hasLeadingIcon = Boolean(resolvedIcon);
  const contentColumnIndex = resolveHeaderContentColumnIndex(hasBack, hasLeadingIcon);
  const iconColumnIndex = resolveHeaderIconColumnIndex(hasBack);
  const contentGridStyle = {
    gridTemplateColumns: buildHeaderContentGridTemplateColumns(hasBack, hasLeadingIcon),
  };

  return (
    <header
      className={[
        headerRootClass,
        isCompress ? headerCompressHeightClass : '',
        isCompress ? 'items-center' : 'items-start',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      <div
        className={headerLeftClass}
      >
        <div
          className={isCompress ? headerContentGridCompressClass : headerContentGridClass}
          style={contentGridStyle}
        >
          {hasBack ? (
            <Button
              variant="tertiary"
              size={headerBackButtonSize[normalizedSize]}
              leftIcon={ArrowLeft}
              customIconSize={headerBackButtonIconSize[normalizedSize]}
              onClick={onBack}
              className={headerBackButtonClass}
              style={{ gridColumn: 1, gridRow: 1 }}
              aria-label={backButtonAriaLabel}
            />
          ) : null}

          {hasLeadingIcon ? (
            <div
              className={headerIconSlotClass}
              style={{ gridColumn: iconColumnIndex, gridRow: 1 }}
            >
              {resolvedIcon}
            </div>
          ) : null}

          <div
            className={headerTitleRowCellClass}
            style={{ gridColumn: contentColumnIndex, gridRow: 1 }}
          >
            {title != null && title !== '' ? (
              <h1
                className={[headerTitleClass[normalizedSize], titleClassName]
                  .filter(Boolean)
                  .join(' ')}
              >
                {title}
              </h1>
            ) : null}
            {resolvedTag}
          </div>

          {resolvedDescription ? (
            <p
              className={[headerDescriptionClass, headerDescriptionCellClass, descriptionClassName]
                .filter(Boolean)
                .join(' ')}
              style={{ gridColumn: contentColumnIndex, gridRow: 2 }}
            >
              {resolvedDescription}
            </p>
          ) : null}
        </div>
      </div>

      {hasActions ? (
        <div className={[headerActionsClass, resolvedDescription ? 'self-start' : 'self-center'].join(' ')}>
          {resolvedSecondaryCta}
          {resolvedPrimaryCta}
        </div>
      ) : null}
    </header>
  );
};

export default Header;
